/**
 * MANAGEMENT (14) — metrics data layer. The two cross-lab aggregation reads behind the redesigned Overview
 * cockpit. Code-verified against server/src/modules/management/metrics.routes.js:
 *   GET /management/metrics/overview   management.lab.read   → OverviewMetrics
 *   GET /management/metrics/trends      management.lab.read   → TrendMetrics
 *
 * CROSS-LAB, NOT lab-scoped — the keys live under the shared `["platform","management","metrics",…]`
 * namespace (a lab switch never invalidates them; one operator session reads the whole estate), mirroring
 * the sibling `managementKeys` factory in ./api.ts. Reads follow the established convention: gate first
 * (`enabled = useIsPlatformAdmin() && online`), then branch on data. A cloud-only route 404/501 on a local
 * box is surfaced via the shared `isManagementAbsent` (re-exported from ./api).
 */
import { useApiQuery } from "@/api";
import type { MetricsPeriod, OverviewMetrics, TrendMetrics } from "./metrics.types";

/** The metrics query keys — rooted under the platform/management namespace, NOT lab-scoped. */
export const metricsKeys = {
  overview: () => ["platform", "management", "metrics", "overview"] as const,
  trends: (period: MetricsPeriod) =>
    ["platform", "management", "metrics", "trends", period] as const,
};

/**
 * GET /management/metrics/overview — point-in-time KPIs + distributions + period-over-period deltas + the
 * alert inputs. Cheap (the server aggregates over indexed counts); a 30s staleTime keeps the cockpit warm
 * without hammering the estate.
 */
export function useOverviewMetrics(enabled: boolean) {
  return useApiQuery<OverviewMetrics>(metricsKeys.overview(), "/management/metrics/overview", {
    enabled,
    staleTime: 30_000,
  });
}

/**
 * GET /management/metrics/trends — the time-series (cumulative labs, tickets opened/resolved) for the chart
 * region, windowed by the chosen period. A 60s staleTime — trends move slowly relative to the KPI counts.
 */
export function useTrendMetrics(period: MetricsPeriod, enabled: boolean) {
  return useApiQuery<TrendMetrics>(metricsKeys.trends(period), "/management/metrics/trends", {
    query: { period },
    enabled,
    staleTime: 60_000,
  });
}
